import 'focus-within-polyfill';

import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ChatState, WebrtcState } from 'sdk';

import { Copyright } from '../../components/copyright';
import { ErrorFallback } from '../../components/errorBoundary';
import { ChatContainer } from '../../components/layout/chatContainer';
import { LoadingView } from '../../components/loadingView';
import { LoadingAnimation } from '../../components/loadingView/animation';
import { ModalNotification } from '../../components/modalNotification';
import { VideoPanel } from '../../components/videoPanel';
import { I18n } from '../../i18n/provider';
import { VIDEO_CALLS_ARE_SUPPORTED } from '../../utils/isVideoCallsSupported';
import { useSDK, useUIState } from '../App';
import { AfterQuestionnaireMessage } from './afterQuestionnaireMessage';
import { AfterSendDialogToEmail } from './afterSendDialogToEmail';
import { BlockedShowcase } from './blockedShowcase';
import { BlockedUser } from './blockedUser';
import { Chat } from './chat';
import { Interaction } from './interaction';
import { OfflineChat } from './offlineChat';
import { OfflineChatGratitude } from './offlineChatGratitude';
import { Prechat } from './prechat';
import { Questionnaire } from './questionnaire';
import { ReloadSuggestion } from './reloadSuggestion';
import { SendDialogToEmail } from './sendDialogToEmail';

const Controller = observer((): ReactElement | null => {
	const sdk = useSDK();
	const { uiStateStore, welcomeDataStore, videoCallsStore } = sdk;
	const { loadingStore, clientViewport } = useUIState();
	useEffect(() => {
		sdk.init();
	}, [sdk]);
	if (!uiStateStore.showAllShowcase) {
		return null;
	}

	if (sdk.uiStateStore.opened && sdk.initRequestStatus.isLoaded && sdk.initRequestStatus.isBlocked) {
		return <LoadingView />;
	}

	return (
		<I18n>
			{((VIDEO_CALLS_ARE_SUPPORTED && videoCallsStore.state === WebrtcState.connected) ||
				videoCallsStore.state === WebrtcState.ringing) &&
				clientViewport.width >= 1000 && <VideoPanel />}
			<ModalNotification />
			{uiStateStore.opened ? (
				<ChatContainer>
					<ErrorBoundary FallbackComponent={ErrorFallback}>
						{loadingStore.showLoading && <LoadingAnimation />}
						{/* экран с пречат+чат */}
						{uiStateStore.chatState === ChatState.prechat && <Prechat />}
						{uiStateStore.chatState === ChatState.chat && <Chat />}
						{/* оффлайн экран */}
						{uiStateStore.chatState === ChatState.offlineChat && <OfflineChat />}
						{uiStateStore.chatState === ChatState.offlineChatGratitude && <OfflineChatGratitude />}
						{/* экран блокировки */}
						{uiStateStore.chatState === ChatState.blockedUser && <BlockedUser />}
						{uiStateStore.chatState === ChatState.blockedShowcase && <BlockedShowcase />}
						{/* экран с опросником */}
						{uiStateStore.chatState === ChatState.questionnaire && <Questionnaire />}
						{uiStateStore.chatState === ChatState.afterQuestionnaireMessage && (
							<AfterQuestionnaireMessage />
						)}
						{/* из localStorage-а исчезли данные, надо перезагрузить страницу */}
						{uiStateStore.chatState === ChatState.missingRequiredData && <ReloadSuggestion />}
						{/* экран отправки диалога на поту */}
						{uiStateStore.chatState === ChatState.sendDialogToEmail && <SendDialogToEmail />}
						{uiStateStore.chatState === ChatState.afterSendDialogToEmail && <AfterSendDialogToEmail />}

						{welcomeDataStore.data?.showcase.showCopyright && <Copyright />}
					</ErrorBoundary>
				</ChatContainer>
			) : null}
			<Interaction />
		</I18n>
	);
});

export default Controller;
